import { z } from 'zod/v4'

import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { assetCategoryOptionSchema } from './asset-form.screen.js'

/*
 * «Ajustes» (`admin.settings`): the four catalogues the settings page paints, each in its own section so that one
 * failing read does not blank the others. Every object is strict: a field that is not named here does not travel,
 * and the columns the page never prints (`createdAt`) are not here. `shared-settings-screen.type-assertions.ts`
 * (backend) ties the language, the two usage objects and the two type rows (the reader rows without `createdAt`) to
 * the code they come from, in both directions.
 */

const count = z.number().int().min(0)

export const adminSettingsLanguageSchema = z.strictObject({
  code: z.string(),
  label: z.string(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
})

/** The columns both simple type catalogues (asset types, platform types) carry. */
const adminCatalogTypeRowFields = {
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
}

export const adminAssetTypeRowSchema = z.strictObject({
  ...adminCatalogTypeRowFields,
  /** What depends on this key: the assets and the asset categories that use it, and their sum. */
  usage: z.strictObject({ assets: count, assetCategories: count, total: count }),
})

export const adminExchangeTypeRowSchema = z.strictObject({
  ...adminCatalogTypeRowFields,
  /** What depends on this key: the exchanges that use it (and the same figure as `total`). */
  usage: z.strictObject({ exchanges: count, total: count }),
})

export const adminSettingsScreen = defineScreen({
  id: 'admin.settings',
  path: '/api/screens/admin/settings',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    languages: screenSection(z.array(adminSettingsLanguageSchema)),
    assetTypes: screenSection(z.array(adminAssetTypeRowSchema)),
    exchangeTypes: screenSection(z.array(adminExchangeTypeRowSchema)),
    assetCategories: screenSection(z.array(assetCategoryOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.settings'],
  budget: { requests: 1, statements: 20, bytes: 60_000, p95Ms: 250 },
  summary: 'Settings: languages, asset types, asset categories and platform types',
})

export type AdminSettingsLanguage = z.infer<typeof adminSettingsLanguageSchema>
export type AdminAssetTypeRow = z.infer<typeof adminAssetTypeRowSchema>
export type AdminExchangeTypeRow = z.infer<typeof adminExchangeTypeRowSchema>
export type AdminSettingsData = ScreenData<typeof adminSettingsScreen>
